"use client";

/**
 * MarketScene — Niche v3 Detail Scene 2 ("The Market").
 *
 * Spec: wireframe-v3.md §3.2 + concept-v3.md DM-3 "$4.2B → $47K".
 *
 * This scene is the main WOW of Niche v3. Its hero is WaterfallScrollPin —
 * pinned right-column chart on desktop, vertical stack on mobile (§6.5).
 *
 * Layout:
 *   - SceneHeader "03 · THE MARKET"
 *   - Scene hook (derived from marketWaterfall values)
 *   - WaterfallScrollPin (pin on desktop, vertical mobile)
 *   - whyNow callout (amber border-l, if present)
 *
 * Fallbacks:
 *   - marketWaterfall missing or incomplete → simple prose callout with
 *     marketSize + marketShareNeeded + revenuePotentialMax
 *   - whyNow missing → callout hidden
 */

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { SceneHeader } from "@/components/shared-primitives/SceneHeader";
import { SceneHookHero } from "@/components/shared-primitives/SceneHookHero";
import {
  WaterfallScrollPin,
  type WaterfallEnrichment,
} from "../shared/WaterfallScrollPin";
import { resolveAccent } from "@/lib/niche-accent-tokens";
import type { NicheCandidateV3 } from "@/types/niche-v3";

interface MarketSceneProps {
  niche: NicheCandidateV3;
}

/**
 * Extract the first clean money token from LLM prose.
 *
 * Why: marketWaterfall.som/tam come from the LLM and often arrive as full
 * sentences — e.g. "$25K/mo realistic slice for pre-launch AI design tool,
 * year-1". We want only "$25K/mo" (or "$560M") for the scene hook, not the
 * descriptive tail, which reads as SOP and kills the dream-sell frame.
 *
 * Prefer `$N[K|M|B]/mo` when present; otherwise fall back to `$N[K|M|B]`.
 */
function extractMoney(s: string | undefined): string | null {
  if (!s) return null;
  const withRate = s.match(/\$\d+(?:\.\d+)?\s*[KMB]?\s*\/\s*(?:mo|yr|month|year)\b/i);
  if (withRate) return withRate[0].replace(/\s+/g, "");
  const bare = s.match(/\$\d+(?:\.\d+)?\s*[KMB]?/i);
  if (bare) return bare[0].replace(/\s+/g, "");
  return null;
}

/**
 * Derive scene hook from marketWaterfall values, with graceful fallback.
 *
 * Voice: dream-sell, MRR-first. The reader's dopamine target is "$25K/mo" —
 * their money — not TAM. So we lead with SOM and let TAM support the image
 * of scale. Earlier iterations read as a reduction ("$240M market. Your
 * slice: $12K/mo") or a shuffle ("$560M is in play. Your first move: $25K/mo
 * realistic slice for pre-launch AI design tool, year-1"). Both primed
 * shrinkage or read as SOP before the waterfall even played.
 */
function deriveSceneHook(niche: NicheCandidateV3): string {
  const mw = niche.marketWaterfall;
  const som = extractMoney(mw?.som);
  const tam = extractMoney(mw?.tam);
  if (som && tam) {
    return `${som}. Out of ${tam} waiting.`;
  }
  if (som) {
    return `${som}. Yours to take.`;
  }
  if (tam) {
    return `${tam} waiting. Your corner is plenty.`;
  }
  if (niche.marketSize && niche.marketShareNeeded) {
    return `${niche.marketSize} in motion. You take ${niche.marketShareNeeded} to get started.`;
  }
  if (niche.marketSize) {
    return `${niche.marketSize} is the pond. You only need your corner of it.`;
  }
  return "A pond bigger than you think. Your corner is plenty.";
}

function formatRevenueRange(min: number, max: number): string | null {
  if (!max || max <= 0) return null;
  const fmt = (n: number) =>
    n >= 1_000 ? `$${Math.round(n / 1_000)}k` : `$${n}`;
  if (min > 0 && min !== max) {
    return `${fmt(min)}–${fmt(max)}/mo`;
  }
  return `${fmt(max)}/mo`;
}

/**
 * Extract member count from a DistributionChannel.notes string.
 *
 * Channel notes are LLM-composed prose. We only surface a clean count badge
 * when the format is unambiguous — e.g. "20M members, ...", "~180K members".
 * Anything else returns null and the UI just renders the channel name.
 */
function parseChannelMembers(notes: string | undefined): string | null {
  if (!notes) return null;
  const m = notes.match(/([~≈<>]?\s*\d+(?:\.\d+)?\s*[KMB]?)\s*members?\b/i);
  if (!m) return null;
  return m[1].replace(/\s+/g, "").toUpperCase();
}

/**
 * Compose WaterfallEnrichment from fields that the niche already ships:
 *   pondChips     ← competitionLevel + difficulty + timeToFirstRevenueWeeks
 *   competitionLevel ← niche.competitionLevel (frame-inversion chip under GAP)
 *   channels      ← distributionChannels top 3 (members parsed from notes)
 *   revenueLadder ← pathForward.monthlyRevenue + timeToFirstRevenueWeeks
 *   microEcho     ← pathForward.nineDayHook
 *   moodDescriptor + accentHex ← visualGradient
 *
 * All slots independently optional — we only push what's present. LLM output
 * stays untouched; we just assemble extra structured data alongside.
 */
function buildEnrichment(niche: NicheCandidateV3): WaterfallEnrichment {
  const enrichment: WaterfallEnrichment = {};

  const pondChips: NonNullable<WaterfallEnrichment["pondChips"]> = [];
  if (niche.competitionLevel) {
    pondChips.push({
      label: "Competition",
      value: niche.competitionLevel.toUpperCase(),
      tone: niche.competitionLevel === "low" ? "emerald" : "neutral",
    });
  }
  if (niche.difficulty) {
    pondChips.push({
      label: "Difficulty",
      value: `${niche.difficulty}/10`,
      tone: "neutral",
    });
  }
  if (niche.timeToFirstRevenueWeeks && niche.timeToFirstRevenueWeeks > 0) {
    pondChips.push({
      label: "First $",
      value: `Week ${niche.timeToFirstRevenueWeeks}`,
      tone: "amber",
    });
  }
  if (pondChips.length > 0) enrichment.pondChips = pondChips;

  if (niche.competitionLevel) {
    enrichment.competitionLevel = niche.competitionLevel;
  }

  if (niche.distributionChannels && niche.distributionChannels.length > 0) {
    enrichment.channels = niche.distributionChannels.slice(0, 3).map((ch) => ({
      name: ch.name,
      members: parseChannelMembers(ch.notes) ?? undefined,
      type: ch.type,
    }));
  }

  const ladder: NonNullable<WaterfallEnrichment["revenueLadder"]> = [];
  if (niche.timeToFirstRevenueWeeks && niche.timeToFirstRevenueWeeks > 0) {
    ladder.push({
      label: `Week ${niche.timeToFirstRevenueWeeks}`,
      value: "First dollar",
    });
  }
  const monthly = niche.pathForward?.monthlyRevenue;
  if (monthly?.month3) {
    ladder.push({ label: "Month 3", value: monthly.month3 });
  }
  if (monthly?.month12) {
    ladder.push({ label: "Month 12", value: monthly.month12, highlight: true });
  }
  if (ladder.length > 0) enrichment.revenueLadder = ladder;

  if (niche.pathForward?.nineDayHook) {
    enrichment.microEcho = niche.pathForward.nineDayHook;
  }

  if (niche.visualGradient) {
    enrichment.moodDescriptor = niche.visualGradient.descriptor;
    enrichment.accentHex = resolveAccent(
      niche.visualGradient.accent,
      niche.visualGradient.mood,
    ).hex;
  }

  return enrichment;
}

export function MarketScene({ niche }: MarketSceneProps) {
  const reduced = useReducedMotion();
  const mw = niche.marketWaterfall;

  const sceneHook = deriveSceneHook(niche);

  // Determine if waterfall has enough data. Need at least tam + som to render
  // the full 4-bar scroll-pin. Everything else is fallback prose.
  const waterfallReady = Boolean(
    mw?.tam?.trim() &&
      mw?.som?.trim() &&
      (mw?.competitionShave?.trim() || mw?.acquisitionShave?.trim()),
  );

  const revenueStr = formatRevenueRange(
    niche.revenuePotentialMin,
    niche.revenuePotentialMax,
  );

  return (
    <section
      id="scene-2-market"
      aria-labelledby="scene-2-title"
      className={cn(
        "relative w-full scroll-mt-20 px-6 py-12 md:py-16 lg:px-12 lg:py-20",
      )}
    >
      <div className="mx-auto w-full max-w-5xl">
        <SceneHeader id="scene-2-title" number={3} title="The market" />
        <SceneHookHero className="max-w-3xl">{sceneHook}</SceneHookHero>
      </div>

      {/* ─── Waterfall (main wow) OR prose fallback ─── */}
      <div className="mx-auto mt-12 w-full max-w-6xl">
        {waterfallReady && mw ? (
          <WaterfallScrollPin
            tam={mw.tam}
            competitionShave={mw.competitionShave}
            acquisitionShave={mw.acquisitionShave}
            som={mw.som}
            enrichment={buildEnrichment(niche)}
          />
        ) : (
          <div
            className={cn(
              "mx-auto max-w-3xl rounded-2xl border border-subtle bg-surface-card p-6",
              "lg:p-8",
            )}
          >
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-amber-300/70">
              Market size
            </p>
            <div className="mt-3 space-y-3 text-white/80">
              {niche.marketSize && (
                <p className="font-heading text-3xl font-bold text-amber-200 lg:text-4xl">
                  {niche.marketSize}
                </p>
              )}
              {niche.marketShareNeeded && (
                <p className="text-lg text-white/70">
                  You need{" "}
                  <span className="font-heading font-bold text-amber-300">
                    {niche.marketShareNeeded}
                  </span>{" "}
                  to hit your slice.
                </p>
              )}
              {revenueStr && (
                <p className="text-lg text-white/70">
                  Realistic range:{" "}
                  <span className="font-heading font-bold text-emerald-300">
                    {revenueStr}
                  </span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ─── whyNow callout ─── */}
      {niche.whyNow && (
        <div className="mx-auto mt-16 w-full max-w-3xl">
          <h3 className="mb-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-amber-300/70">
            Why now
          </h3>
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 16 }}
            whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: reduced ? 0 : 0.5, ease: "easeOut" }}
            className={cn(
              "rounded-xl border-l-2 border-amber-400/60 bg-amber-400/[0.06]",
              "px-6 py-4 text-white/75 lg:text-[16px]",
            )}
          >
            <p className="font-heading italic leading-[1.55] [overflow-wrap:anywhere]">{niche.whyNow}</p>
          </motion.div>
        </div>
      )}
    </section>
  );
}
